"use client";
import { logger } from "@/utils/logger";

import Image from "next/image";
import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronRight, Target, TrendingUp, ShieldCheck, Activity, Briefcase, Lightbulb, Users, Settings, BarChart2, CheckCircle2, Phone, Mail, Sparkles, FolderOpen } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import { useSettings } from "@/context/SettingsContext";

// Fallback Services Data
const fallbackServices = [
  { title: "Requirement analysis", icon: LucideIcons.Target, color: "#0f766e", desc: "Deep-dive analysis of your enterprise needs and strategic objectives, mapping out pathways to optimize operational efficiencies.", colSpan: "lg:col-span-2" },
  { title: "Audit reports", icon: LucideIcons.Briefcase, color: "#2563eb", desc: "Comprehensive, transparent reviews of current operations, vendor costs, and regulatory compliance.", colSpan: "lg:col-span-1" },
  { title: "Bespoke service offerings", icon: LucideIcons.Lightbulb, color: "#7c3aed", desc: "Customized operational models tailored specifically to your unique market position and goals.", colSpan: "lg:col-span-1" },
  { title: "Finance options", icon: LucideIcons.TrendingUp, color: "#059669", desc: "Strategic financial structuring and advanced procurement modeling to preserve capital.", colSpan: "lg:col-span-1" },
  { title: "Fleet management", icon: LucideIcons.Activity, color: "#d97706", desc: "End-to-end optimization of logistical networks, minimizing downtime and maximizing ROI.", colSpan: "lg:col-span-1" },
  { title: "Unbiased recommendations", icon: LucideIcons.ShieldCheck, color: "#db2777", desc: "100% independent, vendor-agnostic advisory ensuring your success is our only metric.", colSpan: "lg:col-span-1" },
  { title: "Complete end to end management", icon: LucideIcons.Users, color: "#0F172A", desc: "Turnkey execution—from high-level strategy formulation to ground-level deployment and ongoing management.", colSpan: "lg:col-span-2" },
];

const partners = [
  { name: "Arun Estates", font: "font-black tracking-tighter" },
  { name: "Vangent", font: "font-serif font-bold italic" },
  { name: "Little Brown Book Group", font: "font-black tracking-widest uppercase" },
  { name: "RAW TV", font: "font-bold tracking-tight" },
  { name: "Linn Maggs Goldwin", font: "font-serif font-black uppercase tracking-tight" },
  { name: "Int. Water Assoc.", font: "font-sans font-bold tracking-[0.1em]" },
];

// Fallback Projects Data
const fallbackProjects = [
  {
    name: "Arun Estates Group",
    type: "Estate agency with 120 branches",
    achievements: [
      "Reduced maintenance cost of existing franking machine fleet by over 40% - £1000's saved per year.",
      "Reduced consumable costs by nearly 50% - ink cartridges and labels.",
      "Reduced management time – quarterly management information provided."
    ]
  },
  {
    name: "International Water Assoc.",
    type: "International charity and publisher",
    achievements: [
      "Negotiated contract reductions with existing suppliers of IT support by 36%.",
      "Improved SLA's agreed – Uptime guarantees now in place.",
      "Colour printing costs reduced by 62% saving £1000's per year.",
      "Reduced calls and line costs by 24%."
    ]
  },
  {
    name: "Vangent",
    type: "Call centre management company",
    achievements: [
      "Refreshed printer fleet with improved productivity and cost reductions of over 30%.",
      "Introduced Franking machines into branch locations – Franked mail up to 38% cheaper than stamps."
    ]
  },
  {
    name: "Little Brown Book Group",
    type: "Publisher",
    achievements: [
      "Reduced colour printing costs by 57%.",
      "Redesigned print room facility improving productivity whilst reducing space required.",
      "Installed secure print and print accounting to improve management information."
    ]
  },
  {
    name: "Linn Maggs Goldwin",
    type: "Accountants",
    achievements: [
      "Assisted in office relocation.",
      "Managed office printing requirements for new office.",
      "Specified and installed document management system including scanning and retrieval."
    ]
  },
  {
    name: "RAW TV",
    type: "Media",
    achievements: [
      "Managed printer refresh programme resulting in cost reductions of 27%.",
      "Installed print management control system.",
      "Managed print service through group reducing management time."
    ]
  }
];

const capabilitiesPills = [
  "Printing", "Mailing", "IT support", "Disaster recovery", "Space planning", "Service provision"
];

export default function ServicesPage() {
  const settings = useSettings();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const [services, setServices] = useState<any[]>(fallbackServices);
  const [projects, setProjects] = useState<any[]>(fallbackProjects);
  const [capabilities, setCapabilities] = useState<any[]>([]);
  const [selectedCapability, setSelectedCapability] = useState<any | null>(null);
  const [latestInsights, setLatestInsights] = useState<any[]>([
    { date: "15 JAN 2026", title: "How to Reduce Colour Printing Costs by Over 50% Without Sacrificing Quality", cat: "Cost Reduction", slug: "" },
    { date: "05 JAN 2026", title: "The Hidden Benefits of Integrating Document Management Systems", cat: "Efficiency", slug: "" },
    { date: "02 JAN 2026", title: "Franking Machines vs Standard Stamps: A Complete Cost Analysis", cat: "Mailing", slug: "" },
  ]);

  const carouselRef = useRef<HTMLDivElement>(null);
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const serviceContentRef = useRef<HTMLDivElement>(null);
  const icon1Ref = useRef<HTMLDivElement>(null);
  const icon2Ref = useRef<HTMLDivElement>(null);
  const sectionTitleRef = useRef<HTMLDivElement>(null);
  const mobileContentRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Floating animations
    if (blob1Ref.current) gsap.to(blob1Ref.current, { y: -15, rotation: 5, duration: 4, yoyo: true, repeat: -1, ease: "sine.inOut" });
    if (blob2Ref.current) gsap.to(blob2Ref.current, { y: 15, duration: 3.5, yoyo: true, repeat: -1, ease: "sine.inOut" });
    if (icon1Ref.current) gsap.to(icon1Ref.current, { y: -20, rotation: 10, duration: 4, yoyo: true, repeat: -1, ease: "sine.inOut" });
    if (icon2Ref.current) gsap.to(icon2Ref.current, { y: 20, rotation: -10, duration: 4.5, yoyo: true, repeat: -1, ease: "sine.inOut", delay: 1 });

    if (sectionTitleRef.current) {
      gsap.fromTo(sectionTitleRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", scrollTrigger: { trigger: sectionTitleRef.current, start: "top 80%" } }
      );
    }
  }, []);

  // Animate service content on change
  useEffect(() => {
    if (serviceContentRef.current) {
      gsap.fromTo(serviceContentRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.3, ease: "power2.out", overwrite: "auto" }
      );
    }
  }, [hoveredIndex, services]);

  // Mobile accordion height animation
  useEffect(() => {
    services.forEach((_, idx) => {
      const el = mobileContentRefs.current[idx];
      if (el) {
        const isActive = hoveredIndex === idx || (hoveredIndex === null && idx === 0);
        gsap.to(el, { height: isActive ? 'auto' : 0, opacity: isActive ? 1 : 0, duration: 0.3, ease: "power2.out", overwrite: "auto" });
      }
    });
  }, [hoveredIndex, services]);

  // Projects scroll animations
  useEffect(() => {
    if (projects.length > 0) {
      const ctx = gsap.context(() => {
        gsap.utils.toArray(".project-card").forEach((el: any, idx) => {
          gsap.fromTo(el,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.5, delay: idx * 0.1, scrollTrigger: { trigger: el, once: true, start: "top 85%" } }
          );
        });
      });
      return () => ctx.revert();
    }
  }, [projects]);

  useEffect(() => {
    const fetchDynamicData = async () => {
      try {
        const [servRes, projRes, capRes] = await Promise.all([
          fetch('/api/public/services'),
          fetch('/api/public/projects'),
          fetch('/api/public/capabilities')
        ]);
        const servJson = await servRes.json();
        const projJson = await projRes.json();
        const capJson = await capRes.json();

        if (servJson.success && servJson.data) {
          if (servJson.data.length === 0) {
            setServices([]);
          } else {
            const mappedServices = servJson.data.map((srv: any, i: number) => {
              const fallback = fallbackServices[i % fallbackServices.length];
              const DynamicIcon = srv.icon ? (LucideIcons as any)[srv.icon] : null;
              return {
                title: srv.title,
                icon: DynamicIcon || fallback.icon,
                customImage: srv.customImage || null,
                color: srv.color || fallback.color,
                desc: srv.description || srv.benefits?.[0] || fallback.desc,
                colSpan: fallback.colSpan,
                slug: srv.slug,
                hasContent: !!srv.content && srv.content.trim() !== '' && srv.content.trim() !== '<p><br></p>'
              };
            });
            setServices(mappedServices);
          }
        }

        if (projJson.success && projJson.data) {
          if (projJson.data.length === 0) {
            setProjects([]);
          } else {
            const mappedProjects = projJson.data.map((proj: any) => ({
              name: proj.title,
              type: proj.clientName || proj.category || "Consultancy Project",
              achievements: proj.results || [proj.description]
            }));
            setProjects(mappedProjects);
          }
        }

        if (capJson.success && capJson.data) {
          setCapabilities(capJson.data);
        }
      } catch (err) {
        logger.error("Failed to fetch services/projects", err);
      }
    };
    fetchDynamicData();
  }, []);

  useEffect(() => {
    const fetchLatestInsights = async () => {
      try {
        const blogRes = await fetch('/api/public/blogs?limit=3');
        const blogJson = await blogRes.json();

        if (blogJson.success && blogJson.data && blogJson.data.length > 0) {
          const mappedBlogs = blogJson.data.map((blog: any) => ({
            date: blog.publishDate ? new Date(blog.publishDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : "RECENTLY",
            title: blog.title,
            cat: blog.category || "General",
            slug: blog.slug
          }));
          setLatestInsights(mappedBlogs);
        }
      } catch (err) {
        logger.error("Failed to fetch latest insights", err);
      }
    };
    fetchLatestInsights();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white font-sans overflow-x-hidden pt-36 md:pt-40 pb-0 relative">

      {/* ══════════════════════════════════════════
          1. CREATIVE HAND-DRAWN HERO (Services Vibe)
      ══════════════════════════════════════════ */}
      <section className="w-full relative min-h-[450px] flex flex-col items-center justify-center text-center px-6 mb-12">

        <div
          ref={blob1Ref}
          className="absolute left-10 lg:left-32 top-1/2 -translate-y-1/2 hidden md:block"
        >
          <svg width="130" height="130" viewBox="0 0 100 100" fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-30">
            <circle cx="35" cy="35" r="15" />
            <circle cx="35" cy="35" r="5" />
            <path d="M35,15 L35,20 M35,50 L35,55 M15,35 L20,35 M50,35 L55,35 M21,21 L24,24 M46,46 L49,49 M21,49 L24,46 M46,21 L49,24" />
            <circle cx="65" cy="65" r="20" />
            <circle cx="65" cy="65" r="7" />
            <path d="M65,40 L65,45 M65,85 L65,90 M40,65 L45,65 M85,65 L90,65 M47,47 L50,50 M80,80 L83,83 M47,83 L50,80 M80,47 L83,50" />
          </svg>
        </div>

        <div
          ref={blob2Ref}
          className="absolute right-10 lg:right-32 top-[30%] hidden md:block"
        >
          <svg width="120" height="120" viewBox="0 0 100 100" fill="none" stroke="#0d9488" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="opacity-30">
            <rect x="20" y="20" width="15" height="15" rx="2" />
            <rect x="65" y="25" width="20" height="20" rx="3" />
            <rect x="40" y="65" width="18" height="18" rx="2" />
            <path d="M35,27 L65,35" strokeDasharray="3 3" />
            <path d="M75,45 L55,65" />
            <path d="M27,35 L40,65" strokeDasharray="3 3" />
          </svg>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center">
          <span className="text-[#0f766e] font-sans font-bold text-[10px] md:text-[11px] tracking-widest uppercase mb-4 block">
            Our Services
          </span>
          <div className="relative mb-8">
            <h1 className="text-[40px] md:text-[56px] font-serif font-extrabold tracking-tight text-gray-900 leading-tight">
              Within each of our business areas we are able to offer
            </h1>
            <svg className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-40 md:w-56 h-6 text-[#0d9488]" viewBox="0 0 100 20" preserveAspectRatio="none">
              <path d="M 5 10 Q 50 16 95 10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M 15 15 Q 50 20 85 15" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" strokeLinecap="round" className="opacity-60" />
            </svg>
          </div>

          <p className="font-sans text-gray-500 text-[14px] md:text-[15px] max-w-2xl leading-relaxed font-medium mb-10">
            Whatever your requirement you can rest assured that the most suitable solution will be recommended without any bias to any one manufacturer
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
            <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
              <Settings className="w-4 h-4 text-[#0d9488]" />
              <span className="text-[12px] font-semibold text-gray-600 tracking-wide font-sans">Complete End-to-End Management</span>
            </div>
            <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
              <BarChart2 className="w-4 h-4 text-[#0d9488]" />
              <span className="text-[12px] font-semibold text-gray-600 tracking-wide font-sans">Unbiased recommendations</span>
            </div>
            <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
              <Target className="w-4 h-4 text-[#0d9488]" />
              <span className="text-[12px] font-semibold text-gray-600 tracking-wide font-sans">Bespoke service offerings</span>
            </div>
          </div>
        </div>
      </section>



      {/* ══════════════════════════════════════════
          2. DIAGONAL INTERACTIVE ACCORDION
      ══════════════════════════════════════════ */}
      <section className="pb-16 md:pb-20 relative bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">

          {/* <div ref={sectionTitleRef} className="mb-14 flex flex-col items-center justify-center text-center px-4 relative"> */}
          {/* <h2 className="text-[32px] md:text-[46px] font-serif font-bold text-gray-900 leading-[1.15] tracking-tight max-w-[800px] mb-6">
              Within each of our business areas we are able to offer:
            </h2> */}
          {/* <div className="w-20 h-1.5 bg-gradient-to-r from-[#0d9488] to-[#3b82f6] rounded-full opacity-80" /> */}

          {/* Subtle background element behind the title to give it more depth */}
          {/* <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[150px] bg-[#0d9488]/5 blur-[60px] pointer-events-none rounded-full z-[-1]" /> */}
          {/* </div> */}

          {/* Interactive Tabbed Showcase (Desktop) - Highly Professional & Trustworthy */}
          {services.length === 0 ? (
            <div className="w-full bg-gray-50/50 rounded-[32px] border border-gray-100 mb-8 overflow-hidden">
              <EmptyState
                title="Services Curating..."
                message="We are finalizing our premium service offerings. Excellence takes time, but it will be worth the wait."
                icon={<Sparkles className="w-8 h-8 text-teal-600 opacity-80" strokeWidth={1.5} />}
              />
            </div>
          ) : (
            <div className="hidden md:flex w-full min-h-[500px] bg-gray-50/50 rounded-[32px] p-6 lg:p-10 border border-gray-100 shadow-sm gap-12 lg:gap-24 relative overflow-hidden">
              {/* Subtle background gradient blob based on active color */}
              <div
                className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[100px] opacity-20 pointer-events-none transition-colors duration-1000"
                style={{ backgroundColor: services[hoveredIndex ?? 0]?.color || '#0d9488' }}
              />

              {/* Left Column: List of Services */}
              <div className="w-[40%] lg:w-[35%] flex flex-col justify-center gap-3 relative z-10">
                {services.map((service, idx) => {
                  const isActive = hoveredIndex === idx || (hoveredIndex === null && idx === 0);
                  return (
                    <div
                      key={idx}
                      onClick={() => setHoveredIndex(idx)}
                      className={`flex items-center gap-5 p-4 rounded-xl cursor-pointer transition-all duration-300 ${isActive
                        ? 'bg-white shadow-[0_10px_30px_rgba(0,0,0,0.06)] scale-[1.02] border border-gray-100'
                        : 'hover:bg-gray-100/50 hover:scale-[1.01] border border-transparent'
                        }`}
                    >
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 overflow-hidden"
                        style={{
                          backgroundColor: isActive ? service.color : '#f3f4f6',
                          color: isActive ? '#ffffff' : '#9ca3af'
                        }}
                      >
                        {service.customImage ? (
                          <Image src={service.customImage} alt={service.title} fill sizes="48px" className="object-cover" />
                        ) : (
                          service.icon && <service.icon size={20} />
                        )}
                      </div>
                      <span className={`font-sans font-bold text-[13px] lg:text-[14px] uppercase tracking-wider transition-colors duration-300 ${isActive ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                        {service.title}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Right Column: Active Content Showcase */}
              <div className="w-[60%] lg:w-[65%] flex flex-col justify-center relative z-10 py-6">
                <div
                  ref={serviceContentRef}
                  className="bg-white w-full h-full rounded-[24px] p-10 lg:p-14 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100 flex flex-col justify-center relative overflow-hidden"
                >
                  {/* Decorative corner accent */}
                  <div
                    className="absolute top-0 right-0 w-32 h-32 rounded-bl-[100px] opacity-10 pointer-events-none transition-colors duration-500"
                    style={{ backgroundColor: services[hoveredIndex ?? 0]?.color || '#0d9488' }}
                  />

                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-10 shadow-sm relative z-10 transition-colors duration-500 overflow-hidden"
                    style={{ backgroundColor: `color-mix(in srgb, ${services[hoveredIndex ?? 0]?.color || '#0d9488'} 15%, transparent)` }}
                  >
                    {(() => {
                      const activeService = services[hoveredIndex ?? 0];
                      if (!activeService) return null;
                      if (activeService.customImage) {
                        return <Image src={activeService.customImage} alt={activeService.title} fill sizes="64px" className="object-cover" />;
                      }
                      const ActiveIcon = activeService.icon;
                      return ActiveIcon ? <ActiveIcon size={28} style={{ color: activeService.color || '#0d9488' }} /> : null;
                    })()}
                  </div>

                  <h2 className="text-[32px] lg:text-[40px] font-serif font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight relative z-10">
                    {services[hoveredIndex ?? 0]?.title}
                  </h2>

                  <p className="font-sans text-[16px] lg:text-[18px] text-gray-500 leading-relaxed font-medium max-w-[500px] relative z-10 mb-8">
                    {services[hoveredIndex ?? 0]?.desc}
                  </p>

                  {services[hoveredIndex ?? 0]?.slug && services[hoveredIndex ?? 0]?.hasContent && (
                    <Link
                      href={`/services/${services[hoveredIndex ?? 0].slug}`}
                      className="inline-flex items-center justify-center self-start gap-2 px-6 py-3 rounded-full text-sm font-bold text-white transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 relative z-10"
                      style={{ backgroundColor: services[hoveredIndex ?? 0]?.color || '#0d9488' }}
                    >
                      View Service Details <ChevronRight size={16} />
                    </Link>
                  )}

                  <div className="mt-8 w-16 h-1.5 rounded-full relative z-10 transition-colors duration-500" style={{ backgroundColor: services[hoveredIndex ?? 0]?.color || '#0d9488' }} />
                </div>
              </div>
            </div>
          )}

          {/* Vertical Accordion Container (Mobile Fallback) */}
          <div className="flex md:hidden flex-col gap-4">
            {services.length === 0 ? (
              <div className="w-full bg-gray-50/50 rounded-[24px] border border-gray-100 overflow-hidden">
                <EmptyState
                  title="Services Curating..."
                  message="We are finalizing our premium service offerings. Excellence takes time."
                  icon={<Sparkles className="w-8 h-8 text-teal-600 opacity-80" strokeWidth={1.5} />}
                />
              </div>
            ) : services.map((service, idx) => {
              const isActive = hoveredIndex === idx || (hoveredIndex === null && idx === 0);
              return (
                <div
                  key={idx}
                  onClick={() => setHoveredIndex(idx)}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden cursor-pointer"
                >
                  <div className="p-6 flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                      style={{ backgroundColor: `color-mix(in srgb, ${service.color || '#0d9488'} 15%, transparent)` }}
                    >
                      {service.customImage ? (
                        <Image src={service.customImage} alt={service.title} fill sizes="(max-width: 768px) 100vw, 33vw" className="object-cover" />
                      ) : (
                        service.icon && <service.icon size={18} style={{ color: service.color }} />
                      )}
                    </div>
                    <h2 className="font-serif text-[18px] font-bold text-gray-900">
                      {service.title}
                    </h2>
                  </div>

                  <div
                    ref={(el) => { mobileContentRefs.current[idx] = el; }}
                    className="px-6 overflow-hidden"
                    style={{ height: isActive ? 'auto' : 0, opacity: isActive ? 1 : 0 }}
                  >
                    <div className="pb-6 pt-2">
                      <p className="font-sans text-[14px] text-gray-500 leading-relaxed mb-4">
                        {service.desc}
                      </p>
                      {service.slug && service.hasContent && (
                        <Link
                          href={`/services/${service.slug}`}
                          className="inline-flex items-center gap-1.5 text-sm font-bold mb-4 hover:underline"
                          style={{ color: service.color || '#0d9488' }}
                        >
                          View Details <ChevronRight size={14} />
                        </Link>
                      )}
                      <div className="w-8 h-1 rounded-full" style={{ backgroundColor: service.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════
          3. TRUST MARQUEE (Creative Teal Section) - CURRENTLY HIDDEN
      ══════════════════════════════════════════ */}
      {/* 
      <section className="py-24 bg-[#0d9488] overflow-hidden flex flex-col items-center justify-center relative">

        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-0" style={{ transform: 'translateY(-1px)' }}>
          <svg className="relative block w-full h-[40px] md:h-[60px]" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M1200 120L0 16.48 0 0 1200 0 1200 120z" fill="#ffffff"></path>
          </svg>
        </div>

        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-black/10 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative z-10 mb-14 flex flex-col items-center px-6">
          <span className="text-teal-100 font-sans font-bold text-[12px] tracking-[0.2em] uppercase mb-4 text-center">Trusted By</span>
          <h3 className="text-[28px] md:text-[34px] font-serif font-bold text-white tracking-tight text-center max-w-lg leading-tight drop-shadow-sm">
            Reducing Costs. Improving Efficiency.
          </h3>
        </div>

        <div className="w-full relative flex overflow-hidden group py-6 border-y border-white/10 bg-black/5 backdrop-blur-sm">
          <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-[#0d9488] to-transparent z-20 pointer-events-none" />
          <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-[#0d9488] to-transparent z-20 pointer-events-none" />

          <div className="flex shrink-0 animate-marquee whitespace-nowrap items-center min-w-full">
            {[...partners, ...partners].map((partner, idx) => (
              <div key={idx} className="flex shrink-0 items-center justify-center mx-12 md:mx-20">
                <span className={`text-[24px] md:text-[28px] text-teal-100/70 hover:text-white hover:scale-105 transition-all duration-500 cursor-default drop-shadow-sm ${partner.font}`}>
                  {partner.name}
                </span>
              </div>
            ))}
          </div>
          <div className="flex shrink-0 animate-marquee whitespace-nowrap items-center min-w-full" aria-hidden="true">
            {[...partners, ...partners].map((partner, idx) => (
              <div key={idx} className="flex shrink-0 items-center justify-center mx-12 md:mx-20">
                <span className={`text-[24px] md:text-[28px] text-teal-100/70 hover:text-white hover:scale-105 transition-all duration-500 cursor-default drop-shadow-sm ${partner.font}`}>
                  {partner.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-0" style={{ transform: 'translateY(1px) rotate(180deg)' }}>
          <svg className="relative block w-full h-[40px] md:h-[60px]" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M1200 120L0 16.48 0 0 1200 0 1200 120z" fill="#0a0f1c"></path>
          </svg>
        </div>
      </section>
      */}

      {/* ══════════════════════════════════════════
          4. OUR PROJECTS (Data-Driven Responsive Grid)
      ══════════════════════════════════════════ */}
      <section className="py-20 md:py-24 bg-[#0a0f1c] relative overflow-hidden" ref={carouselRef}>

        {/* Curvy Top Divider (Transitions from white section above) */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-0" style={{ transform: 'translateY(-1px)' }}>
          <svg className="relative block w-full h-[40px] md:h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#ffffff"></path>
          </svg>
        </div>

        {/* Abstract Background for dark section */}
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0d9488] via-[#0a0f1c] to-[#0a0f1c]" />

        {/* Large Subtle Graph Paper Pattern over the whole section for a "consultancy / data" vibe */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSJub25lIiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMSIvPjwvc3ZnPg==')" }} />

        <div className="max-w-[1400px] mx-auto px-6 relative z-10 mb-20 flex flex-col items-center text-center">
          <span className="text-[#0d9488] font-sans font-bold text-[10px] tracking-[0.2em] uppercase mb-4 block">Our Projects</span>
          <h2 className="text-[28px] font-serif font-bold text-white leading-[1.1] tracking-tight max-w-[700px] mb-4">
            Proven Results
          </h2>
          <p className="text-[13px] text-gray-400 font-sans max-w-[600px] leading-relaxed font-medium">
            Explore our portfolio of consultancy projects across various sectors.
          </p>
        </div>

        {/* The Responsive Grid */}
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          {projects.length === 0 ? (
            <div className="w-full py-8">
              <EmptyState
                title="Coming Soon"
                message="We are currently updating our project portfolio."
                icon={<FolderOpen className="w-8 h-8 text-teal-600 opacity-80" strokeWidth={1.5} />}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {projects.map((project, idx) => (
                <div
                  key={idx}
                  className="project-card opacity-0 group relative bg-[#ffffff]/[0.02] border border-white/10 hover:border-[#0d9488]/40 rounded-3xl p-8 lg:p-10 flex flex-col transition-all duration-300 hover:bg-[#ffffff]/[0.04] overflow-hidden hover:shadow-[0_20px_40px_rgba(13,148,136,0.05)] hover:-translate-y-1"
                >
                  {/* Individual Card Graph Pattern (Subtle) */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none" style={{ backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSJub25lIiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMSIvPjwvc3ZnPg==')" }} />

                  <div className="relative z-10 flex flex-col h-full">
                    <div className="mb-6">
                      <span className="inline-block px-3 py-1 bg-[#0d9488]/10 text-[#0d9488] font-sans font-bold text-[9px] tracking-widest uppercase rounded-full mb-4">
                        {project.type}
                      </span>
                      <h3 className="text-[16px] md:text-[18px] font-serif font-bold text-white leading-tight">
                        {project.name}
                      </h3>
                    </div>

                    {/* Elegant Divider */}
                    <div className="w-full h-[1px] bg-white/10 mb-6 relative">
                      <div className="absolute left-0 top-0 h-[1px] w-12 bg-[#0d9488] transition-all duration-500 group-hover:w-full" />
                    </div>

                    <ul className="space-y-5 mt-auto">
                      {project.achievements && project.achievements.map((achievement: any, aIdx: number) => (
                        <li key={aIdx} className="flex items-start gap-4">
                          <div className="w-4 h-4 rounded-full bg-[#0d9488]/20 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-[#0d9488] transition-colors duration-300">
                            <CheckCircle2 className="w-3 h-3 text-[#0d9488] group-hover:text-white transition-colors duration-300" />
                          </div>
                          <span className="font-sans text-[13px] text-gray-300 leading-relaxed font-medium">
                            {achievement}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          5. INSIGHTS PREVIEW
      ══════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-white relative">
        {/* Creative Angle Divider from Dark Section */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-0" style={{ transform: 'translateY(-99%)' }}>
          <svg className="relative block w-full h-[30px] md:h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M1200 120L0 16.48 0 0 1200 0 1200 120z" fill="#0a0f1c"></path>
          </svg>
        </div>
        <div className="max-w-[1000px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <h2 className="text-[28px] font-serif font-bold text-gray-900 mb-3 tracking-tight">Latest Insights</h2>
              <p className="text-gray-500 font-sans font-medium text-[13px]">
                Expert perspectives on cost reduction, operational efficiency, and smarter procurement.
              </p>
            </div>
            <Link href="/insights" className="text-[11px] font-sans font-bold text-[#0f766e] hover:text-[#0F172A] transition-colors flex items-center gap-1 uppercase tracking-widest">
              View All <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="flex flex-col">
            {latestInsights.map((insight, idx) => (
              <Link href={insight.slug ? `/insights/${insight.slug}?from=services` : "/insights?from=services"} key={idx} className="group flex flex-col md:flex-row md:items-center py-6 border-b border-gray-100 hover:border-gray-300 transition-colors">
                <div className="md:w-[20%] mb-3 md:mb-0">
                  <span className="text-[10px] font-sans font-bold text-gray-500 tracking-[0.1em]">{insight.date}</span>
                </div>
                <div className="md:w-[60%] mb-3 md:mb-0 pr-6">
                  <h3 className="text-[16px] md:text-[18px] font-serif font-bold text-gray-900 group-hover:text-[#0d9488] transition-colors leading-tight">
                    {insight.title}
                  </h3>
                </div>
                <div className="md:w-[20%] flex md:justify-end">
                  <span className="inline-block bg-gray-50 text-gray-500 text-[9px] font-sans font-bold px-3 py-1.5 rounded-full uppercase tracking-widest border border-gray-100 group-hover:bg-[#0d9488] group-hover:text-white group-hover:border-[#0d9488] transition-all">
                    {insight.cat}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          6. ELEGANT ANIMATED CTA (Unique Borderless Design)
      ══════════════════════════════════════════ */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-gray-50 flex items-center justify-center">

        {/* Creative Wavy Divider from Insights Section */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-0" style={{ transform: 'translateY(-1px)' }}>
          <svg className="relative block w-full h-[40px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#ffffff"></path>
          </svg>
        </div>

        {/* Unique Background Node/Line Pattern */}
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCI+PHBhdGggZD0iTTAgNDBoODBNNDAgMHY4MCIgc3Ryb2tlPSIjMEYxNzJBIiBzdHJva2Utd2lkdGg9IjAuNSIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjQwIiBjeT0iNDAiIHI9IjMiIGZpbGw9IiMwRjE3MkEiLz48L3N2Zz4=')" }} />

        {/* Floating Abstract Icons in Open Space */}
        <div ref={icon1Ref} className="absolute top-32 left-10 md:left-32 opacity-[0.03] pointer-events-none z-0">
          <Target size={180} strokeWidth={1} />
        </div>
        <div ref={icon2Ref} className="absolute bottom-20 right-10 md:right-32 opacity-[0.03] pointer-events-none z-0">
          <TrendingUp size={180} strokeWidth={1} />
        </div>

        {/* Massive Ambient Glow (Replaces the need for a card) */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[radial-gradient(circle,_var(--tw-gradient-stops))] from-[#0d9488]/10 via-[#3b82f6]/5 to-transparent rounded-full blur-[100px] pointer-events-none z-0" />

        <div className="max-w-[1000px] mx-auto px-6 relative z-20 w-full flex flex-col items-center text-center">

          {/* Central Anchor Icon */}
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center shadow-[0_10px_40px_rgba(13,148,136,0.15)] mb-10 text-[#0d9488] relative z-10 border border-gray-100">
            <ShieldCheck size={32} strokeWidth={1.5} />
          </div>

          <h2 className="text-[28px] md:text-[42px] font-serif font-bold text-gray-900 leading-[1.1] mb-8 tracking-tight relative z-10 max-w-[800px]">
            All consultancy projects are <br className="hidden md:block" /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0d9488] to-[#3b82f6]">free of charge.</span>
          </h2>

          <p className="text-[16px] md:text-[18px] text-gray-500 font-sans font-medium max-w-[650px] mb-14 leading-relaxed relative z-10">
            To find out more or for a free, no obligation consultation please {settings.contactPhone?.trim() ? <>call <strong className="text-gray-800"><a href={`tel:${settings.contactPhone}`} className="hover:text-[#0d9488] transition-colors">{settings.contactPhone}</a></strong> or email us at</> : "email us at"} <strong className="text-gray-800"><a href={`mailto:${settings.contactEmail}`} className="hover:text-[#0d9488] transition-colors">{settings.contactEmail}</a></strong>
          </p>

          <div className="flex flex-wrap justify-center gap-3 md:gap-5 mb-16 max-w-[800px] relative z-10">
            {capabilities.length > 0 ? (
              capabilities.map((cap, idx) => {
                const stripHtml = (html: any) => (html || '').replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim();
                const sDesc = stripHtml(cap.shortDescription);
                const lDesc = stripHtml(cap.longDescription);
                const hasDetails = (sDesc.length > 0 && sDesc.toLowerCase() !== 'n/a') || (lDesc.length > 0 && lDesc.toLowerCase() !== 'n/a');

                return hasDetails ? (
                  <Link
                    key={idx}
                    href={`/capabilities/${cap.slug}`}
                    className="inline-block bg-white/60 backdrop-blur-sm border border-gray-200/60 text-gray-600 hover:text-[#0d9488] hover:bg-white hover:border-[#0d9488]/30 font-sans font-bold text-[11px] md:text-[13px] px-6 py-3 rounded-full transition-all cursor-pointer shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md hover:scale-105 hover:-translate-y-0.5"
                  >
                    {cap.title}
                  </Link>
                ) : (
                  <span
                    key={idx}
                    className="inline-block bg-white/60 backdrop-blur-sm border border-gray-200/60 text-gray-600 hover:text-[#0d9488] hover:bg-white hover:border-[#0d9488]/30 font-sans font-bold text-[11px] md:text-[13px] px-6 py-3 rounded-full transition-all cursor-default shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md hover:scale-105 hover:-translate-y-0.5"
                  >
                    {cap.title}
                  </span>
                );
              })
            ) : (
              capabilitiesPills.map((pill, idx) => (
                <span
                  key={idx}
                  className="inline-block bg-white/60 backdrop-blur-sm border border-gray-200/60 text-gray-600 hover:text-[#0d9488] hover:bg-white hover:border-[#0d9488]/30 font-sans font-bold text-[11px] md:text-[13px] px-6 py-3 rounded-full transition-all cursor-default shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md hover:scale-105 hover:-translate-y-0.5"
                >
                  {pill}
                </span>
              ))
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full relative z-10">
            {settings.contactPhone?.trim() && (
              <a href={`tel:${settings.contactPhone}`} className="group flex items-center justify-center gap-3 bg-[#0F172A] hover:bg-gray-800 px-10 py-5 rounded-full text-[15px] font-sans font-bold text-white transition-all shadow-[0_10px_20px_rgba(0,0,0,0.1)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.15)] hover:-translate-y-1 w-full sm:w-auto">
                <Phone className="w-4 h-4 text-[#0d9488]" /> {settings.contactPhone}
              </a>
            )}
            <a href={`mailto:${settings.contactEmail}`} className="group flex items-center justify-center gap-3 bg-white border border-gray-200 hover:border-gray-300 px-10 py-5 rounded-full text-[15px] font-sans font-bold text-gray-900 transition-all shadow-[0_5px_15px_rgba(0,0,0,0.05)] hover:shadow-md hover:-translate-y-1 w-full sm:w-auto">
              <Mail className="w-4 h-4 text-[#3b82f6]" /> {settings.contactEmail}
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
